import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import Confetti from 'react-confetti';

import { useAuth } from '../context/AuthContext';
import api, { platformAPI } from '../services/api';
import { SPECIAL_CARDS } from '../whot/utils/constants';
import WhotGameTable from '../whot/components/WhotGameTable';
import { buildInviteLink } from '../utils/inviteLinks';
import WhotPlayerHand from '../whot/components/WhotPlayerHand';
import WhotControls from '../whot/components/WhotControls';
import {
  getGlobalSoundEnabled,
  setGlobalSoundEnabled,
  subscribeToGlobalSoundPreference,
} from '../utils/soundPreferences';
import {
  connectWhotOnlineSocket,
  whotOnlineSocket,
} from '../services/whotOnlineSocket';
import { getSocket } from '../services/socket';
import '../styles/Flappy.css';
import '../styles/Whot.css';
import QuickChat from '../components/QuickChat';

export default function WhotOnlineRoom() {
  const REPLAY_ERROR_TOAST_ID = 'whot:replay:error';
  const { roomId } = useParams();
  const [searchParams] = useSearchParams();
  const urlTournamentId = searchParams.get('tournamentId');
  const [game, setGame] = useState(null);
  const activeTournamentId = game?.tournamentId || urlTournamentId;
  const navigate = useNavigate();
  const { user, token, refreshUser } = useAuth();
  const localUserId = user?.id;
  const [error, setError] = useState('');
  const [rematchLoading, setRematchLoading] = useState(false);
  const [replayRequests, setReplayRequests] = useState([]);
  const [showConfetti, setShowConfetti] = useState(false);
  const [turnTimeLeftMs, setTurnTimeLeftMs] = useState(null);
  const [multiplayerResults, setMultiplayerResults] = useState(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [liveChargePercent, setLiveChargePercent] = useState(0);
  const [isPortrait, setIsPortrait] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [showChat, setShowChat] = useState(false);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    return getGlobalSoundEnabled();
  });
  const warnedCountsRef = useRef(new Map());
  const settledChallengeRef = useRef(null);
  const landscapeNoticeShownRef = useRef(false);
  const timeoutGameOverToastRef = useRef(0);
  const gameOverToastRef = useRef(0);
  const lastForfeitRef = useRef(0);
  const lastTurnOwnerRef = useRef(null);
  const chatListRef = useRef(null);
  const chatOpenRef = useRef(false);
  const isMountedRef = useRef(false);
  const activeTournamentIdRef = useRef(activeTournamentId);

  useEffect(() => {
    activeTournamentIdRef.current = activeTournamentId;
  }, [activeTournamentId]);

  const lastSpokenRef = useRef({
    map: new Map(),
    lastTopCardId: null,
    lastPick2Count: 0,
    lastPick3Active: false,
  });

  const speakOnce = useCallback((key, text, cooldownMs = 2500) => {
    if (!text) return;
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    if (!soundEnabled) return;
    const now = Date.now();
    const last = lastSpokenRef.current.map.get(key) || 0;
    if (now - last < cooldownMs) return;
    lastSpokenRef.current.map.set(key, now);
    try {
      const utterance = new SpeechSynthesisUtterance(text);
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utterance);
    } catch (err) {
      // Ignore speech failures quietly
    }
  }, [soundEnabled]);

  useEffect(() => {
    setGlobalSoundEnabled(soundEnabled);
  }, [soundEnabled]);

  useEffect(() => subscribeToGlobalSoundPreference(setSoundEnabled), []);

  useEffect(() => {
    chatOpenRef.current = showChat;
  }, [showChat]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // Replay navigates to a new room/challenge in-place; clear stale settlement/result state.
    setMultiplayerResults(null);
    setResultsLoading(false);
    settledChallengeRef.current = null;
    landscapeNoticeShownRef.current = false;
  }, [roomId]);

  useEffect(() => {
    if (game?.gameStatus !== 'in_progress') return;
    if (!isPortrait) return;
    if (landscapeNoticeShownRef.current) return;

    landscapeNoticeShownRef.current = true;
    toast('Tip: rotate to landscape for better gameplay view.', {
      icon: '↔️',
      duration: 9000,
      id: 'whot-landscape-tip',
    });
  }, [game?.gameStatus, isPortrait]);

  useEffect(() => {
    if (!token) return;
    const socket = connectWhotOnlineSocket(token);
    const spectate = searchParams.get('spectate') === '1';
    const joinFn = spectate ? whotOnlineSocket.spectateRoom : whotOnlineSocket.joinRoom;

    const joinCurrentRoom = () => {
      joinFn({ roomId }, (resp) => {
        if (!isMountedRef.current) return;
        if (!resp?.ok) {
          const message = resp?.error || 'Failed to join room';
          if (message === 'Game already finished') {
            whotOnlineSocket.spectateRoom({ roomId }, (spectateResp) => {
              if (!isMountedRef.current) return;
              if (spectateResp?.ok) {
                setError('');
                setIsReconnecting(false);
                return;
              }
              const spectateMessage = spectateResp?.error || 'Failed to spectate finished game';
              setError(spectateMessage);
              toast.error(spectateMessage);
            });
            return;
          }
          // If not allowed to join as player, automatically retry as spectator
          if (message && /not accepting new players|room is full|not a participant/i.test(message)) {
            // Only redirect if not already spectating
            if (!spectate) {
              const params = new URLSearchParams(searchParams);
              params.set('spectate', '1');
              navigate(`/whot-online/room/${roomId}?${params.toString()}`, { replace: true });
              return;
            }
            setError('You are watching as a spectator.');
            return;
          }
          if (!navigator.onLine || /transport|socket|network/i.test(message)) {
            setIsReconnecting(true);
            return;
          }
          setError(message);
          toast.error(message);
          return;
        }
        setError('');
        setIsReconnecting(false);
      });
    };

    const onState = (state) => {
      if (state?.roomId !== roomId) return;
      setError('');
      setIsReconnecting(false);
      setGame(state);
    };

    const onError = (payload) => {
      const message = payload?.message || 'Action rejected';
      if (/transport|socket|network/i.test(String(message))) {
        setIsReconnecting(true);
        return;
      }
      toast.error(message);
    };

    whotOnlineSocket.onState(onState);
    whotOnlineSocket.onError(onError);
    whotOnlineSocket.onReplayRequest((payload) => {
      if (!payload?.requestId) return;
      setReplayRequests((prev) => {
        const existing = prev.find((req) => req.requestId === payload.requestId);
        if (!existing) return [...prev, payload];
        return prev.map((req) => (req.requestId === payload.requestId ? { ...req, ...payload } : req));
      });
      if (payload?.requestedBy?.id !== user?.id) {
        toast('Replay request received');
      }
    });
    whotOnlineSocket.onReplayUpdate((payload) => {
      if (!payload?.requestId) return;
      setReplayRequests((prev) => prev.map((req) => (req.requestId === payload.requestId ? { ...req, ...payload } : req)));
    });
    whotOnlineSocket.onReplayCancel((payload) => {
      setReplayRequests((prev) => prev.filter((req) => req.requestId !== payload?.requestId));
      if (payload?.reason) toast.error(payload.reason);
    });
    whotOnlineSocket.onReplayReady((payload) => {
      setReplayRequests((prev) => prev.filter((req) => req.requestId !== payload?.requestId));
      if (payload?.newRoomId) {
        navigate(`/whot-online/room/${payload.newRoomId}`);
      }
    });
    whotOnlineSocket.onChatHistory((payload) => {
      if (String(payload?.roomId) !== String(roomId)) return;
      setChatMessages(Array.isArray(payload?.messages) ? payload.messages : []);
      setUnreadChatCount(0);
    });
    whotOnlineSocket.onChatMessage((payload) => {
      if (String(payload?.roomId) !== String(roomId)) return;
      setChatMessages((prev) => [...prev, payload.message].slice(-40));
      if (!chatOpenRef.current && Number(payload?.message?.userId) !== Number(localUserId)) {
        setUnreadChatCount((prev) => prev + 1);
      }
    });
    whotOnlineSocket.onEliminated((payload) => {
      toast.error(`${payload?.message || 'You have been eliminated!'}`);
      setTimeout(() => {
        const tId = activeTournamentIdRef.current;
        if (tId) {
          navigate(`/whot/tournaments/${tId}`);
        } else {
          navigate('/whot-online');
        }
      }, 5000);
    });

    socket.on('connect', joinCurrentRoom);
    socket.on('disconnect', () => setIsReconnecting(true));
    socket.on('connect_error', () => setIsReconnecting(true));
    joinCurrentRoom();

    const handleOffline = () => setIsReconnecting(true);
    const handleOnline = () => {
      setIsReconnecting(false);
      joinCurrentRoom();
    };
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      socket.off('connect', joinCurrentRoom);
      socket.off('disconnect');
      socket.off('connect_error');
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      whotOnlineSocket.removeListeners();
    };
  }, [token, roomId, navigate, searchParams, user?.id, localUserId]);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await platformAPI.getSettings();
        const charge = Number(res?.data?.chargePercent);
        if (Number.isFinite(charge) && charge >= 0) {
          setLiveChargePercent(charge);
        }
      } catch {
        // ignore settings failures
      }
    };

    loadSettings();
  }, []);

  useEffect(() => {
    const updateOrientation = () => {
      const portrait = window.innerHeight > window.innerWidth;
      setIsPortrait(portrait);
    };

    updateOrientation();
    window.addEventListener('resize', updateOrientation);
    window.addEventListener('orientationchange', updateOrientation);
    return () => {
      window.removeEventListener('resize', updateOrientation);
      window.removeEventListener('orientationchange', updateOrientation);
    };
  }, []);

  useEffect(() => {
    const el = chatListRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chatMessages]);

  useEffect(() => {
    if (showChat) {
      setUnreadChatCount(0);
    }
  }, [showChat]);



  useEffect(() => {
    if (!game || game.gameStatus !== 'finished') return undefined;
    const isWinner = Number(game.winner?.id) === Number(localUserId);
    if (!isWinner) {
      setShowConfetti(false);
      return undefined;
    }

    setShowConfetti(true);
    const timer = window.setTimeout(() => {
      setShowConfetti(false);
    }, 7000);
    return () => window.clearTimeout(timer);
  }, [game, localUserId]);

  useEffect(() => {
    if (!game || game.gameStatus !== 'in_progress' || !game.turnDeadlineAt || game.turnTimeoutMs <= 0) {
      setTurnTimeLeftMs(null);
      return undefined;
    }

    const update = () => {
      const remaining = Math.max(0, Number(game.turnDeadlineAt) - Date.now());
      setTurnTimeLeftMs(remaining);
    };

    update();
    const timer = window.setInterval(update, 250);
    return () => window.clearInterval(timer);
  }, [game]);

  useEffect(() => {
    const forfeitAt = Number(game?.lastForfeit?.at || 0);
    if (!forfeitAt || forfeitAt === lastForfeitRef.current) return;
    lastForfeitRef.current = forfeitAt;
    const forfeit = game?.lastForfeit || {};
    const name = forfeit?.name || 'A player';
    const reason = String(forfeit?.reason || '').toLowerCase();
    const count = Number(forfeit?.count || 0);
    const limit = Number(forfeit?.limit || 3);
    const isMine = String(forfeit?.userId || '') === String(localUserId || '');

    if (reason === 'timeout-three-strikes') {
      const winnerName = game?.winner?.name || 'N/A';
      const detail = `${name} missed turn time ${count}/${limit} and was removed from the match. Winner: ${winnerName}.`;
      if (isMine) {
        toast.error(`Game Over. ${detail}`, {
          id: `whot-timeout-three-strikes-${roomId}-${forfeitAt}`,
          duration: 8000,
        });
      } else {
        toast(`Game Over. ${detail}`, {
          icon: '⏱️',
          id: `whot-timeout-three-strikes-${roomId}-${forfeitAt}`,
          duration: 8000,
        });
      }
      return;
    }

    if (isMine) {
      toast(`You missed your turn (${count}/${limit}).`, { icon: '⏱️', duration: 3000 });
    } else {
      toast(`${name} missed turn (${count}/${limit}).`, { icon: '⏱️', duration: 3000 });
    }
  }, [game?.lastForfeit, localUserId]);

  useEffect(() => {
    if (game?.gameStatus !== 'finished') return;
    const reason = String(game?.lastForfeit?.reason || '').toLowerCase();
    if (reason !== 'timeout-three-strikes') return;
    const at = Number(game?.lastForfeit?.at || 0);
    if (!at || at === gameOverToastRef.current) return;
    gameOverToastRef.current = at;

    const forfeit = game?.lastForfeit || {};
    const loserName = forfeit?.name || 'A player';
    const winnerName = game?.winner?.name || 'N/A';
    const strikeCount = Number(forfeit?.count || 0);
    const strikeLimit = Number(forfeit?.limit || 3);
    toast.error(
      `Game Over. ${loserName} missed turn time ${strikeCount}/${strikeLimit} and was removed from the match. Winner: ${winnerName}.`,
      {
        id: `whot-timeout-final-${roomId}-${at}`,
        duration: 9000,
      }
    );
  }, [game?.gameStatus, game?.lastForfeit, game?.winner?.name, roomId]);

  useEffect(() => {
    if (game?.gameStatus !== 'finished' || !activeTournamentId) return undefined;
    
    // Auto-redirect to tournament page after 5 seconds
    const timer = window.setTimeout(() => {
      navigate(`/whot/tournaments/${activeTournamentId}`);
    }, 5000);

    return () => window.clearTimeout(timer);
  }, [game?.gameStatus, activeTournamentId, navigate]);

  useEffect(() => {
    if (game?.gameStatus !== 'finished') return;
    const reason = String(game?.lastForfeit?.reason || '').toLowerCase();
    if (!reason.includes('timeout') || reason.includes('auto-skip') || reason.includes('three-strikes')) return;
    const at = Number(game?.lastForfeit?.at || 0);
    if (!at || at === timeoutGameOverToastRef.current) return;
    timeoutGameOverToastRef.current = at;

    const winnerName = game?.winner?.name || 'N/A';
    toast.success(`Game over by timeout. Winner: ${winnerName}.`, {
      id: `whot-timeout-game-over-${roomId}`,
      duration: 7000,
    });
  }, [game?.gameStatus, game?.lastForfeit?.reason, game?.lastForfeit?.at, game?.winner?.name, roomId]);

  useEffect(() => {
    if (!game || game.gameStatus !== 'in_progress') return;
    const activeTurnId = String(game.turnPlayerId || game.players?.[game.currentPlayerIndex || 0]?.id || '');
    if (!activeTurnId || activeTurnId === lastTurnOwnerRef.current) return;
    lastTurnOwnerRef.current = activeTurnId;

    const isMyTurnNow = String(localUserId || '') === activeTurnId;
    if (isMyTurnNow) {
      toast.success('Your turn. Play now.', { id: `whot-turn-${roomId}` });
      speakOnce('turn-alert', 'Your turn');
    } else {
      toast.dismiss(`whot-turn-${roomId}`);
    }
  }, [game, localUserId, roomId, speakOnce]);

  const players = game?.players || [];
  const currentPlayer = players[game?.currentPlayerIndex || 0] || null;
  const topCard = (game?.discardPile || [])[game?.discardPile?.length - 1] || null;
  const isRoomFull = Number.isFinite(game?.maxPlayers)
    ? players.length >= Number(game.maxPlayers)
    : true;
  const isHost = String(game?.creatorId || '') === String(localUserId || '');
  const spectating = searchParams.get('spectate') === '1';
  const isSpectator = spectating || !players.some((p) => String(p.id) === String(localUserId));
  const isBettingRoom = Number(game?.wager || 0) > 0 || Boolean(game?.challengeId);
  const canPlay = game?.gameStatus === 'in_progress' && !isSpectator;
  const canHostStart = !isSpectator && isHost && game?.gameStatus === 'waiting' && ((game?.players || []).filter((p) => !p?.isBot).length >= 2);
  const isHumanTurn = canPlay && String(currentPlayer?.id || '') === String(localUserId || '');
  const awaitingShapeCall = canPlay && String(game?.awaitingShapeCallPlayerId || '') === String(localUserId || '');
  const pendingPick2Count = (game?.pendingPick2?.active && isHumanTurn)
    ? (game.pendingPick2?.count || 2)
    : 0;
  const pendingPick3Active = Boolean(game?.pendingPick3?.active && isHumanTurn);
  const pickBannerText = pendingPick2Count > 0
    ? `Pick ${pendingPick2Count}`
    : pendingPick3Active
      ? 'Pick 3 or counter with a 5'
      : '';
  const turnTimeSeconds = Number.isFinite(game?.turnTimeoutMs)
    ? Math.round(game.turnTimeoutMs / 1000)
    : null;
  const currentTurnName = game?.turnPlayerId
    ? (players.find((p) => String(p.id) === String(game.turnPlayerId))?.name || '—')
    : (currentPlayer?.name || '—');
  const formattedTurnTimeLeft = useMemo(() => {
    if (!Number.isFinite(turnTimeLeftMs)) return null;
    const totalSeconds = Math.ceil(turnTimeLeftMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const paddedSeconds = String(seconds).padStart(2, '0');
    return `${minutes}:${paddedSeconds}`;
  }, [turnTimeLeftMs]);

  const payoutSummary = useMemo(() => {
    if (!isBettingRoom) return null;
    const placement = multiplayerResults?.placement || [];
    if (!placement.length) return null;
    const totalPaid = placement.reduce((sum, entry) => sum + Number(entry.prize || 0), 0);
    const grossPot = Number(multiplayerResults?.grossPot || 0)
      || (Number(game?.wager || 0) * placement.length);
    const parsedChargePercent = Number(multiplayerResults?.chargePercent);
    const chargePercent = Number.isFinite(parsedChargePercent) && parsedChargePercent >= 0
      ? parsedChargePercent
      : Number(liveChargePercent || 0);
    const parsedChargeAmount = Number(multiplayerResults?.chargeAmount);
    const chargeAmount = Number.isFinite(parsedChargeAmount) && parsedChargeAmount >= 0
      ? parsedChargeAmount
      : Math.floor((grossPot * chargePercent) / 100);
    const parsedNetPot = Number(multiplayerResults?.netPot);
    const netPot = Number.isFinite(parsedNetPot) && parsedNetPot >= 0
      ? parsedNetPot
      : Math.max(0, grossPot - chargeAmount);
    return {
      placement,
      totalPaid,
      grossPot,
      chargeAmount,
      netPot,
      chargePercent,
    };
  }, [isBettingRoom, multiplayerResults, game?.wager, liveChargePercent]);

  const gameOverSummary = useMemo(() => {
    if (!game || game.gameStatus !== 'finished') return null;
    const forfeit = game?.lastForfeit || null;
    const reason = String(forfeit?.reason || '').toLowerCase();
    const loserName = forfeit?.name || 'A player';
    const winnerName = game?.winner?.name || 'N/A';
    const strikeCount = Number(forfeit?.count || 0);
    const strikeLimit = Number(forfeit?.limit || 3);

    if (reason === 'timeout-three-strikes') {
      return {
        title: 'Game Over',
        detail: `${loserName} missed turn time ${strikeCount}/${strikeLimit} and was removed from the match. Winner: ${winnerName}.`,
      };
    }

    if (reason.includes('timeout') && !reason.includes('auto-skip')) {
      return {
        title: 'Game Over',
        detail: `The match ended by timeout. Winner: ${winnerName}.`,
      };
    }

    return {
      title: 'Game Over',
      detail: `Winner: ${winnerName}.`,
    };
  }, [game]);

  const buildMultiplayerResults = useCallback((placement = [], prizes = {}, meta = {}) => {
    const nameMap = new Map(
      (players || []).map((p) => [Number(p.id), p.name || `Player ${p.id}`])
    );
    const normalized = (placement || []).map((entry) => {
      const userId = Number(entry.userId);
      return {
        userId,
        rank: entry.rank,
        score: entry.score,
        prize: Number(prizes?.[userId] || 0),
        name: nameMap.get(userId) || `Player ${userId}`,
      };
    });
    normalized.sort((a, b) => a.rank - b.rank);
    return {
      placement: normalized,
      chargePercent: Number(meta?.chargePercent),
      chargeAmount: Number(meta?.chargeAmount),
      netPot: Number(meta?.netPot),
      grossPot: Number(meta?.grossPot),
    };
  }, [players]);

  useEffect(() => {
    if (!game || !Array.isArray(game.players) || isSpectator) return;
    const warnMap = warnedCountsRef.current;

    game.players.forEach((p) => {
      if (!p || p.id === user?.id) return;
      const count = Array.isArray(p.cards) ? p.cards.length : 0;
      const prev = warnMap.get(p.id) || null;

      if ((count === 2 || count === 1) && prev !== count) {
        const label = count === 1 ? 'last card' : 'two cards';
        toast(`${p.name} has ${label}!`, {
          icon: '⚠️',
          id: `whot-warn-${p.id}`,
          duration: 3000,
        });
        speakOnce(`cards-${p.id}-${count}`, `${p.name} has ${label}`);
      }

      warnMap.set(p.id, count);
    });
  }, [game, user?.id, isSpectator, speakOnce]);

  useEffect(() => {
    return () => {
      toast.dismiss();
    };
  }, []);

  useEffect(() => {
    if (!game || game.gameStatus !== 'finished') return;
    if (!game.challengeId) return;
    const isSpectatingMode = searchParams.get('spectate') === '1';
    if (isSpectatingMode) return;
    const isPlayer = (players || []).some((p) => String(p.id) === String(localUserId));
    if (localUserId && !isPlayer) return;
    const challengeKey = String(game.challengeId);
    if (settledChallengeRef.current === challengeKey) return;

    settledChallengeRef.current = challengeKey;
    const rawHandScores = game.results?.handScores || {};
    const rawHandsRemaining = game.results?.handsRemaining || {};
    let payload = { handsRemaining: rawHandsRemaining, handScores: rawHandScores };

    if (!Object.keys(rawHandScores).length && game.winner?.id && Array.isArray(game.players)) {
      const fallbackScores = {};
      game.players.forEach((player) => {
        if (!player?.id) return;
        fallbackScores[String(player.id)] = String(player.id) === String(game.winner.id) ? 0 : 999;
      });
      payload = { handsRemaining: {}, handScores: fallbackScores };
    }

    api.post(`/multiplayer/complete/${game.challengeId}`, {
      handsRemaining: payload.handsRemaining,
      handScores: payload.handScores,
    }).then(() => {
      refreshUser();
    }).catch((err) => {
      const message = err?.response?.data?.error || 'Failed to settle wager';
      toast.error(message);
      settledChallengeRef.current = null;
    });
  }, [game, localUserId, players, searchParams, refreshUser]);

  useEffect(() => {
    if (!game || game.gameStatus !== 'finished') {
      setResultsLoading(false);
      return;
    }
    if (!game.challengeId) return;
    if (multiplayerResults?.placement?.length) {
      setResultsLoading(false);
      return;
    }

    let cancelled = false;
    const shouldShowLoading = isBettingRoom;
    if (shouldShowLoading) setResultsLoading(true);

    let fallbackTimerId = null;
    let fallbackIntervalId = null;
    const maxAttempts = 8;
    let attempts = 0;

    const fetchResults = async () => {
      if (cancelled) return;
      try {
        const res = await api.get(`/multiplayer/${game.challengeId}`);
        const challenge = res.data?.challenge;
        if (!challenge) return;
        if (!Array.isArray(challenge.placement) || challenge.placement.length === 0) return;
        setMultiplayerResults(buildMultiplayerResults(
          challenge.placement,
          challenge.prize_distribution,
          {
            chargePercent: challenge.charge_percent,
            chargeAmount: challenge.charge_amount,
            netPot: challenge.net_pot,
            grossPot: Number(challenge.wager || 0) * Number(challenge.current_players || challenge.placement.length || 0),
          }
        ));
        if (shouldShowLoading) setResultsLoading(false);
        refreshUser();
        cancelled = true;
        if (fallbackTimerId) window.clearTimeout(fallbackTimerId);
        if (fallbackIntervalId) window.clearInterval(fallbackIntervalId);
      } catch (e) {
        // ignore
      }
    };

    const startFallbackPolling = () => {
      if (fallbackIntervalId) return;
      fallbackIntervalId = window.setInterval(() => {
        attempts += 1;
        if (attempts > maxAttempts) {
          window.clearInterval(fallbackIntervalId);
          fallbackIntervalId = null;
          if (shouldShowLoading) setResultsLoading(false);
          return;
        }
        fetchResults();
      }, 1500);
    };

    fetchResults();
    fallbackTimerId = window.setTimeout(() => {
      if (!cancelled) startFallbackPolling();
    }, 5000);

    return () => {
      cancelled = true;
      if (fallbackTimerId) window.clearTimeout(fallbackTimerId);
      if (fallbackIntervalId) window.clearInterval(fallbackIntervalId);
    };
  }, [game, multiplayerResults, buildMultiplayerResults, refreshUser, isBettingRoom]);

  useEffect(() => {
    if (!game?.challengeId || !localUserId) return;
    const socket = getSocket();
    if (!socket) return;
    const roomName = `challenge-${game.challengeId}`;
    socket.emit('join-room', {
      room: roomName,
      challengeId: parseInt(game.challengeId, 10),
      userId: localUserId
    });
    socket.emit('multiplayer:join-challenge', {
      challengeId: parseInt(game.challengeId, 10),
      userId: localUserId
    });

    const onGameComplete = (data) => {
      if (String(data?.challengeId) !== String(game.challengeId)) return;
      if (!Array.isArray(data?.placement)) return;
      setMultiplayerResults(buildMultiplayerResults(data.placement, data.prizes || {}, {
        chargePercent: data.chargePercent,
        chargeAmount: data.chargeAmount,
        netPot: data.netPot,
        grossPot: data.grossPot,
      }));
      setResultsLoading(false);
    };

    socket.on('multiplayer:game-complete', onGameComplete);
    return () => {
      socket.off('multiplayer:game-complete', onGameComplete);
    };
  }, [game?.challengeId, localUserId, buildMultiplayerResults]);

  const activeRules = game?.houseRules || {};

  useEffect(() => {
    if (!game || isSpectator) return;
    const tracker = lastSpokenRef.current;
    if (activeRules.pick2 && pendingPick2Count > 0 && tracker.lastPick2Count !== pendingPick2Count) {
      speakOnce('pick2', `Pick ${pendingPick2Count}`);
    }
    tracker.lastPick2Count = pendingPick2Count;

    if (activeRules.pick3On5 && pendingPick3Active && !tracker.lastPick3Active) {
      speakOnce('pick3', 'Pick three or counter with a five');
    }
    tracker.lastPick3Active = pendingPick3Active;
  }, [game, pendingPick2Count, pendingPick3Active, isSpectator, speakOnce, activeRules]);

  useEffect(() => {
    if (!game || isSpectator) return;
    const top = topCard;
    if (!top || !top.id) return;
    const tracker = lastSpokenRef.current;
    if (tracker.lastTopCardId === top.id) return;
    tracker.lastTopCardId = top.id;

    if (top.number === SPECIAL_CARDS.GENERAL_MARKET && activeRules.generalMarket14) {
      speakOnce('general-market', 'General market');
    } else if (top.number === SPECIAL_CARDS.SUSPENSION && activeRules.suspension8) {
      speakOnce('suspension', 'Suspension');
    } else if (top.number === SPECIAL_CARDS.HOLD_ON && activeRules.holdOn1) {
      speakOnce('hold-on', 'Hold on');
    }
  }, [game, topCard, isSpectator, speakOnce, activeRules]);

  const myHand = useMemo(
    () => players.find((p) => String(p.id) === String(localUserId || ''))?.cards || [],
    [players, localUserId]
  );
  const opponents = useMemo(
    () => players.filter((p) => String(p.id) !== String(localUserId || '')),
    [players, localUserId]
  );

  const sendAction = useCallback((payload) => {
    whotOnlineSocket.sendAction({ roomId, ...payload });
  }, [roomId]);

  const onPlayCard = useCallback((card) => {
    if (!card) return;
    if (!canPlay) {
      toast.error('Waiting for other players to join.');
      return;
    }
    sendAction({ type: 'play', cardId: card.id });
  }, [sendAction, canPlay]);

  const onDrawCard = useCallback(() => {
    if (!canPlay) {
      toast.error('Waiting for other players to join.');
      return;
    }
    sendAction({ type: 'draw' });
  }, [sendAction, canPlay]);

  const onCallShape = useCallback((shape) => {
    if (!shape) return;
    if (!canPlay) return;
    sendAction({ type: 'callShape', shape });
  }, [sendAction, canPlay]);

  const roomInviteLink = buildInviteLink(`/whot-online/room/${roomId}`);

  const copyRoomInviteLink = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(roomInviteLink);
        toast.success('Invite link copied');
      } else {
        window.prompt('Copy WHOT invite link:', roomInviteLink);
      }
    } catch {
      window.prompt('Copy WHOT invite link:', roomInviteLink);
    }
  };

  const sendChatMessage = () => {
    const text = String(chatInput || '').trim();
    if (!text) return;
    whotOnlineSocket.sendChat({ roomId, text }, (resp) => {
      if (!resp?.ok) {
        toast.error(resp?.error || 'Failed to send message');
        return;
      }
      setChatInput('');
    });
  };

  const startRoom = () => {
    whotOnlineSocket.startRoom({ roomId }, (resp) => {
      if (!resp?.ok) {
        toast.error(resp?.error || 'Failed to start room');
      } else {
        toast.success('Game started');
      }
    });
  };

  const isClosedRoomError = String(error || '').trim().toLowerCase() === 'room not found';

  if (error) {
    return (
      <div className="chess-match-page">
        <div className="cm-topbar">
          <button className="cm-icon-btn" onClick={() => activeTournamentId ? navigate(`/whot/tournaments/${activeTournamentId}`) : navigate('/whot-online')}>←</button>
          <div className="cm-topbar-center"><span className="cm-topbar-title">WHOT</span></div>
          <span style={{ width: 36 }} />
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>{isClosedRoomError ? 'Game Over' : 'Room Error'}</div>
            <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, marginBottom: 16 }}>{isClosedRoomError ? 'Game over, Room closed' : error}</div>
            {activeTournamentId ? (
              <button className="cm-btn cm-btn--primary" onClick={() => navigate(`/whot/tournaments/${activeTournamentId}`)}>Back to Arena</button>
            ) : (
              <button className="cm-btn cm-btn--primary" onClick={() => activeTournamentId ? navigate(`/whot/tournaments/${activeTournamentId}`) : navigate('/whot-online')}>
                {activeTournamentId ? 'Back to Arena' : 'Back to Lobby'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="chess-match-page">
        <div className="cm-topbar">
          <button className="cm-icon-btn" onClick={() => activeTournamentId ? navigate(`/whot/tournaments/${activeTournamentId}`) : navigate('/whot-online')}>←</button>
          <div className="cm-topbar-center"><span className="cm-topbar-title">WHOT</span></div>
          <span style={{ width: 36 }} />
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.55)', fontSize: 14 }}>
            Connecting to room {roomId}…
          </div>
        </div>
      </div>
    );
  }

  if (game.gameStatus === 'finished') {
    const primaryReplayRequest = replayRequests[0] || null;
    const acceptedIds = new Set(primaryReplayRequest?.acceptedIds || []);
    const hasAcceptedReplay = acceptedIds.has(user?.id);
    const replayExpiresAt = primaryReplayRequest?.expiresAt ? new Date(primaryReplayRequest.expiresAt) : null;
    const replayTimeLeftSec = replayExpiresAt
      ? Math.max(0, Math.ceil((replayExpiresAt.getTime() - Date.now()) / 1000))
      : null;

    return (
      <div className="chess-match-page">
        {showConfetti && <Confetti />}
        {isSpectator && (
          <div style={{
            background: 'rgba(30, 41, 59, 0.85)',
            color: '#fff',
            padding: '8px 0',
            textAlign: 'center',
            fontWeight: 600,
            fontSize: 15,
            letterSpacing: 0.5,
            borderBottom: '2px solid #fbbf24',
            marginBottom: 8,
            zIndex: 10,
          }}>
            👀 You are watching as a spectator
          </div>
        )}

        {/* Top bar */}
        <div className="cm-topbar">
          <button className="cm-icon-btn" onClick={() => setSoundEnabled((prev) => !prev)} title="Toggle sound">{soundEnabled ? '🔊' : '🔇'}</button>
          <div className="cm-topbar-center">
            <span className="cm-topbar-title">WHOT</span>
            {isBettingRoom && <span className="cm-badge cm-badge--gold">💰 {game.wager}</span>}
          </div>
          <button className="cm-icon-btn" onClick={() => activeTournamentId ? navigate(`/whot/tournaments/${activeTournamentId}`) : navigate('/whot-online')}>✕</button>
        </div>

        {/* Result banner */}
        {(() => {
          const isWinner = Number(game.winner?.id) === Number(localUserId);
          const cls = isWinner ? 'cm-result-banner--win' : 'cm-result-banner--loss';
          const icon = isWinner ? '🏆' : '😞';
          return (
            <div className={`cm-result-banner ${cls}`}>
              <span className="cm-result-banner__icon">{icon}</span>
              <span>Winner: {game.winner?.name || '—'}</span>
            </div>
          );
        })()}

        {gameOverSummary ? (
          <div
            style={{
              margin: '10px 14px 0',
              padding: '12px 14px',
              borderRadius: 12,
              background: 'rgba(239, 68, 68, 0.14)',
              border: '1px solid rgba(239, 68, 68, 0.28)',
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 4 }}>{gameOverSummary.title}</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.82)', lineHeight: 1.5 }}>
              {gameOverSummary.detail}
            </div>
          </div>
        ) : null}

        {/* Payout summary */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
          {payoutSummary ? (
            <div style={{ width: '100%', maxWidth: 420, background: 'rgba(255,255,255,0.06)', borderRadius: 12, padding: '14px 16px' }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>Payout Summary</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', display: 'grid', gap: 3 }}>
                <span>Total pot: {payoutSummary.grossPot} wikipenny</span>
                <span>Charge ({payoutSummary.chargePercent}%): {payoutSummary.chargeAmount} wikipenny</span>
                <span>Net pot: {payoutSummary.netPot} wikipenny</span>
              </div>
              <div style={{ marginTop: 10, display: 'grid', gap: 4 }}>
                {payoutSummary.placement.map((entry) => (
                  <div key={`payout-${entry.userId}`} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span>{entry.name}</span>
                    <span style={{ fontWeight: 600 }}>{entry.prize} wp</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {isBettingRoom && resultsLoading && !multiplayerResults?.placement?.length ? (
            <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 13 }}>Results loading…</div>
          ) : null}

          {multiplayerResults?.placement?.length ? (
            <div style={{ width: '100%', maxWidth: 420, background: 'rgba(255,255,255,0.06)', borderRadius: 12, padding: '14px 16px' }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>Results</div>
              <div style={{ display: 'grid', gap: 6 }}>
                {multiplayerResults.placement.map((p) => (
                  <div key={p.userId} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    <span>#{p.rank} {p.name}</span>
                    <span style={{ color: 'rgba(255,255,255,0.55)' }}>Cards: {p.score} • Prize: {p.prize} wp</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {/* Friendly game scoreboard — shown when there's no wager/betting */}
          {!isBettingRoom && !multiplayerResults?.placement?.length && game.gameStatus === 'finished' && (() => {
            const handScores = game.results?.handScores || {};
            const sortedPlayers = [...(game.players || [])].sort((a, b) => {
              const scoreA = Number(handScores[String(a.id)] ?? (String(a.id) === String(game.winner?.id) ? 0 : 999));
              const scoreB = Number(handScores[String(b.id)] ?? (String(b.id) === String(game.winner?.id) ? 0 : 999));
              return scoreA - scoreB;
            });
            if (sortedPlayers.length === 0) return null;
            return (
              <div style={{ width: '100%', maxWidth: 420, background: 'rgba(255,255,255,0.06)', borderRadius: 12, padding: '14px 16px' }}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>📊 Final Standings</div>
                <div style={{ display: 'grid', gap: 6 }}>
                  {sortedPlayers.map((p, i) => {
                    const isWinner = String(p.id) === String(game.winner?.id);
                    const cardScore = Number(handScores[String(p.id)] ?? (isWinner ? 0 : '—'));
                    const cardCount = Array.isArray(p.cards) ? p.cards.length : null;
                    return (
                      <div
                        key={p.id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          fontSize: 13,
                          padding: '7px 10px',
                          borderRadius: 8,
                          background: isWinner
                            ? 'rgba(250,204,21,0.12)'
                            : 'rgba(255,255,255,0.04)',
                          border: isWinner
                            ? '1px solid rgba(250,204,21,0.35)'
                            : '1px solid rgba(255,255,255,0.06)',
                        }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontWeight: 700, color: isWinner ? '#facc15' : 'rgba(255,255,255,0.6)', minWidth: 20 }}>
                            {isWinner ? '🏆' : `#${i + 1}`}
                          </span>
                          <span style={{ fontWeight: isWinner ? 700 : 400 }}>{p.name || `Player ${p.id}`}</span>
                          {String(p.id) === String(localUserId) && (
                            <span style={{ fontSize: 10, background: 'rgba(99,102,241,0.3)', padding: '1px 5px', borderRadius: 4, color: 'rgba(255,255,255,0.7)' }}>You</span>
                          )}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12, textAlign: 'right' }}>
                          {cardScore !== '—'
                            ? `Score: ${cardScore}`
                            : cardCount !== null
                              ? `Cards left: ${cardCount}`
                              : 'Winner'}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div style={{ marginTop: 10, fontSize: 11, color: 'rgba(255,255,255,0.35)', textAlign: 'center' }}>
                  Score = sum of card values remaining in hand. Lower is better.
                </div>
              </div>
            );
          })()}

          {/* Replay request */}
          {primaryReplayRequest && !primaryReplayRequest.newRoomId && (
            <div style={{ width: '100%', maxWidth: 420, background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.25)', borderRadius: 12, padding: '14px 16px' }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>Replay Request</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>
                Players: {(primaryReplayRequest.players || []).map((p) => p.name || p.id).join(', ')}
                {Number(primaryReplayRequest.wager || 0) > 0 && ` • Wager: ${primaryReplayRequest.wager}`}
                {replayTimeLeftSec !== null && ` • ${replayTimeLeftSec}s left`}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button
                  className="cm-btn cm-btn--primary"
                  disabled={hasAcceptedReplay}
                  onClick={() => {
                    whotOnlineSocket.respondReplay({ requestId: primaryReplayRequest.requestId, accept: true }, (resp) => {
                      if (!resp?.ok) toast.error(resp?.error || 'Failed to accept replay');
                    });
                  }}
                >
                  {hasAcceptedReplay ? 'Accepted' : 'Accept'}
                </button>
                <button
                  className="cm-btn cm-btn--ghost"
                  onClick={() => {
                    whotOnlineSocket.respondReplay({ requestId: primaryReplayRequest.requestId, accept: false }, (resp) => {
                      if (!resp?.ok) toast.error(resp?.error || 'Failed to reject replay');
                    });
                  }}
                >
                  Reject
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="cm-actions">
          {!activeTournamentId && (
            <button
              className="cm-btn cm-btn--primary"
              onClick={() => {
                if (rematchLoading) return;
                setRematchLoading(true);
                whotOnlineSocket.requestReplay({ roomId }, (resp) => {
                  setRematchLoading(false);
                  if (!resp?.ok) {
                    toast.error(resp?.error || 'Failed to request replay', {
                      id: REPLAY_ERROR_TOAST_ID,
                      duration: 4000,
                    });
                    return;
                  }
                  if (resp?.newRoomId) {
                    navigate(`/whot-online/room/${resp.newRoomId}`);
                    return;
                  }
                  toast('Replay request sent');
                });
              }}
            >
              {rematchLoading ? 'Requesting…' : 'Request Replay'}
            </button>
          )}

          {activeTournamentId ? (
            <button className="cm-btn cm-btn--primary" onClick={() => navigate(`/whot/tournaments/${activeTournamentId}`)}>
              Back to Arena
            </button>
          ) : (
            <button className="cm-btn cm-btn--ghost" onClick={() => activeTournamentId ? navigate(`/whot/tournaments/${activeTournamentId}`) : navigate('/whot-online')}>
              Lobby
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="whot-page whot-page--room whot-page--game">
      {isSpectator && (
        <div style={{
          background: 'rgba(30, 41, 59, 0.85)',
          color: '#fff',
          padding: '8px 0',
          textAlign: 'center',
          fontWeight: 600,
          fontSize: 15,
          letterSpacing: 0.5,
          borderBottom: '2px solid #fbbf24',
          marginBottom: 8,
          zIndex: 10,
        }}>
          👀 You are watching as a spectator
        </div>
      )}

      {showChat && (
        <div
          onClick={() => setShowChat(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            zIndex: 50,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 12,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: 400,
              maxHeight: 'min(70vh, 520px)',
              borderRadius: 16,
              border: '1px solid rgba(255,255,255,0.1)',
              background: 'rgba(19,23,34,0.96)',
              overflow: 'hidden',
              boxShadow: '0 18px 60px rgba(0,0,0,0.35)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>Room Chat</div>
              <button className="cm-icon-btn" onClick={() => setShowChat(false)} title="Close chat">✕</button>
            </div>
            <div ref={chatListRef} style={{ maxHeight: 'calc(70vh - 116px)', overflowY: 'auto', padding: '10px 12px', display: 'grid', gap: 8 }}>
              {chatMessages.length === 0 && (
                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>No messages yet.</div>
              )}
              {chatMessages.map((message) => (
                <div key={message.id} style={{ fontSize: 12, lineHeight: 1.4 }}>
                  <div style={{ color: Number(message.userId) === Number(localUserId) ? '#facc15' : 'rgba(255,255,255,0.75)', fontWeight: 700 }}>
                    {message.username}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.88)' }}>{message.text}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, padding: 10, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendChatMessage();
                  }
                }}
                maxLength={240}
                placeholder={isSpectator ? 'Spectator chat' : 'Type a message'}
                style={{ flex: 1, borderRadius: 10, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(0,0,0,0.2)', color: '#fff', padding: '10px 12px', outline: 'none' }}
              />
              <button className="cm-btn cm-btn--primary" onClick={sendChatMessage}>
                Send
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Compact top bar ── */}
      <div className="cm-topbar" style={{ background: 'rgba(0,0,0,0.35)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <button className="cm-icon-btn" onClick={() => activeTournamentId ? navigate(`/whot/tournaments/${activeTournamentId}`) : navigate('/whot-online')} title="Back to lobby">←</button>
        <div className="cm-topbar-center">
          <span className="cm-topbar-title">WHOT #{roomId}</span>
          {isBettingRoom && <span className="cm-badge cm-badge--gold">💰 {game.wager}</span>}
          {isSpectator && <span className="cm-badge">👁 Spectating</span>}
          {!isRoomFull && game?.gameStatus !== 'in_progress' && (
            <span className="cm-badge">{players.length}/{game.maxPlayers}</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          <button className="cm-icon-btn" onClick={copyRoomInviteLink} title="Copy invite link">📋</button>
          <button className="cm-icon-btn" onClick={() => setSoundEnabled((prev) => !prev)} title="Toggle sound">{soundEnabled ? '🔊' : '🔇'}</button>
        </div>
      </div>

      {game?.gameStatus === 'waiting' && (
        <div className="cm-status-line" style={{ justifyContent: 'center' }}>
          {canHostStart
            ? 'Another player joined. Press Start to begin.'
            : `Waiting in lobby • ${players.length} player${players.length !== 1 ? 's' : ''} joined`}
        </div>
      )}

      {canHostStart && (
        <div 
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 45
          }}
        >
          <button 
            onClick={startRoom}
            title="Start Game"
            style={{
              background: '#10b981',
              color: '#fff',
              border: 'none',
              borderRadius: 999,
              width: 72,
              height: 72,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 12px 32px rgba(16, 185, 129, 0.4), inset 0 2px 4px rgba(255,255,255,0.4)',
              transition: 'transform 0.2s',
            }}
            onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            <svg style={{ width: 36, height: 36, marginLeft: 4 }} viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z"/>
            </svg>
          </button>
        </div>
      )}

      {/* Exit button for non-host players waiting for game to start */}
      {game?.gameStatus === 'waiting' && !isHost && !isSpectator && (
        <div
          style={{
            position: 'absolute',
            bottom: 32,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 45,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <div style={{
            fontSize: 12,
            color: 'rgba(255,255,255,0.45)',
            textAlign: 'center',
            marginBottom: 2,
          }}>
            Host hasn't started yet
          </div>
          <button
            id="whot-waiting-exit-btn"
            onClick={() => {
              whotOnlineSocket.leaveRoom({ roomId }, (resp) => {
                if (!resp?.ok) {
                  toast.error(resp?.error || 'Failed to leave room');
                } else {
                  if (activeTournamentId) {
                    navigate(`/whot/tournaments/${activeTournamentId}`);
                  } else {
                    navigate('/whot-online');
                  }
                }
              });
            }}
            style={{
              background: 'rgba(239,68,68,0.15)',
              color: '#f87171',
              border: '1.5px solid rgba(239,68,68,0.45)',
              borderRadius: 12,
              padding: '10px 28px',
              fontSize: 14,
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              letterSpacing: 0.3,
              transition: 'background 0.18s, border-color 0.18s',
              backdropFilter: 'blur(6px)',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'rgba(239,68,68,0.30)';
              e.currentTarget.style.borderColor = 'rgba(239,68,68,0.75)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'rgba(239,68,68,0.15)';
              e.currentTarget.style.borderColor = 'rgba(239,68,68,0.45)';
            }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 16, height: 16 }}>
              <path d="M10.09 15.59L11.5 17l5-5-5-5-1.41 1.41L12.67 11H3v2h9.67l-2.58 2.59zM19 3H5a2 2 0 00-2 2v4h2V5h14v14H5v-4H3v4a2 2 0 002 2h14a2 2 0 002-2V5a2 2 0 00-2-2z"/>
            </svg>
            Leave Room
          </button>
        </div>
      )}

      <div className="whot-shell" style={{ flex: 1, minHeight: 0 }}>
        <WhotGameTable
          topRow={opponents.length > 0 ? (
            <div className="whot-opponents-row">
              {opponents.map((p) => (
                <WhotPlayerHand
                  key={p.id}
                  cards={p.cards}
                  topCard={topCard}
                  currentShape={game.currentShape}
                  isCurrentPlayer={false}
                  playerName={p.name}
                  isOpponent={true}
                  streak={p.streak}
                  containerClassName={`whot-opponent-panel ${String(p.id) === String(currentPlayer?.id) ? 'whot-opponent-panel--active' : ''}`}
                />
              ))}
            </div>
          ) : null}
          topCard={topCard}
          deckRemaining={game.deckRemaining}
          currentShape={game.currentShape}
          turnLabel={game.gameStatus === 'in_progress' ? currentTurnName : null}
          turnTimeLeft={formattedTurnTimeLeft}
          pickBannerText={pickBannerText}
          currentPlayerName={currentPlayer?.name || '—'}
          players={players}
          onDrawCard={onDrawCard}
          isCurrentPlayer={isHumanTurn}
          awaitingShapeCall={awaitingShapeCall}
          onCallShape={onCallShape}
        >
          <WhotPlayerHand
            cards={myHand}
            topCard={topCard}
            currentShape={game.currentShape}
            isCurrentPlayer={isHumanTurn && !awaitingShapeCall}
            pendingPick2Active={Boolean(game.pendingPick2?.active && isHumanTurn)}
            pendingPick3Active={Boolean(game.pendingPick3?.active && isHumanTurn)}
            disablePlayableHighlight={isBettingRoom || !!activeTournamentId}
            playerName={user?.username || 'You'}
            streak={user?.whot_streak}
            onPlayCard={onPlayCard}
            hiddenCardIds={[]}
            disabled={awaitingShapeCall || !canPlay}
            containerClassName={`whot-card--in-table ${isHumanTurn ? 'whot-hand--active-turn' : ''}`}
          />

          <WhotControls
            onDrawCard={onDrawCard}
            onOpenChat={() => setShowChat(true)}
            unreadChatCount={unreadChatCount}
            onRestart={() => toast('Restart not available in online rooms', { id: 'no-restart' })}
            onLeave={() => activeTournamentId ? navigate(`/whot/tournaments/${activeTournamentId}`) : navigate('/whot-online')}
            onStopConfetti={() => {}}
            canDraw={isHumanTurn && canPlay && !awaitingShapeCall}
            gameStatus={game.gameStatus}
            showDrawButton={canPlay}
            showStopConfetti={false}
            pendingPick2Count={pendingPick2Count}
            pendingPick3Active={pendingPick3Active}
          />
        </WhotGameTable>
      </div>

      <QuickChat 
        socket={whotOnlineSocket.socket || (typeof getSocket === 'function' ? getSocket() : null)} 
        roomId={roomId} 
        meId={localUserId} 
        gameType="whot"
      />
    </div>
  );
}

